import { useCampaignDateRange } from '/@/hooks/crop-monitoring/use-campaign-date-range';
import type { CropMonitoring } from '/@/types/crop-monitoring';

export interface CampaignWindow {
    /** Campaign start as YYYY-MM-DD (undefined until the fallow zone resolves). */
    dateGte?: string;
    /** Campaign end as YYYY-MM-DD (the harvest date, or far in the future while the campaign is open). */
    dateLte: string;
    /** Crop-protection lower bound: campaign start − 2 months. */
    cpDateGte?: string;
    /** Crop-protection upper bound: the campaign end. */
    cpDateLte: string;
    /** True once the field and campaign dates are known (safe to enable date-filtered fetches). */
    windowReady: boolean;
    isLoading: boolean;
}

/**
 * The campaign date window shared by every registries query — the timeline itself and the
 * stage banners. Registers are bounded to `[start, end]`; crop-protection recommendations to
 * `[start−2mo, end]` — only the lower bound is shifted, because a recommendation can be created
 * up to ~2 months before the campaign starts but never after it ends. Shifting the upper bound
 * too would hide recommendations made in the last two months of a harvested campaign.
 * Centralised here so the many callers derive the same params and their SWR requests dedupe.
 */
export const useCampaignWindow = ({
    field,
    cropMonitoring,
}: {
    field?: number;
    cropMonitoring?: CropMonitoring;
}): CampaignWindow => {
    const { start, end, isLoading } = useCampaignDateRange({ field, cropMonitoring });
    const dateGte = start?.format('YYYY-MM-DD');
    const dateLte = end.format('YYYY-MM-DD');
    const cpDateGte = start?.subtract(2, 'month').format('YYYY-MM-DD');
    const cpDateLte = dateLte;
    const windowReady = Boolean(field) && Boolean(dateGte && dateLte);
    return { dateGte, dateLte, cpDateGte, cpDateLte, windowReady, isLoading };
};
